import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const RadiotherapyTechnologyCoursePage: React.FC = () => {
  const radiotherapyData = {
    title: "Bachelor of Science in Radiotherapy Technology",
    shortName: "B.Sc Radiotherapy",
    duration: "3-4 Years",
    description: "A specialized program focusing on cancer treatment using radiation therapy technology and techniques for therapeutic applications.",
    overview: "B.Sc Radiotherapy Technology prepares students to become skilled radiation therapists who deliver precise radiation treatments to cancer patients. The program covers radiation physics, oncology, treatment planning, radiation safety, and patient care. Students learn to operate sophisticated radiation therapy equipment, work with multidisciplinary cancer care teams, and provide compassionate care to patients undergoing cancer treatment.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology/Mathematics",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)", 
      "Age: 17-25 years",
      "Medical fitness certificate required",
      "Good physical and mental health",
      "Ability to work with cancer patients"
    ],
    careerProspects: {
      roles: [
        "Radiation Therapist",
        "Medical Dosimetrist", 
        "Radiation Safety Officer",
        "Cancer Care Specialist",
        "Treatment Planning Technologist",
        "Radiation Therapy Coordinator",
        "Medical Physics Assistant",
        "Oncology Technologist",
        "Quality Assurance Technologist",
        "Radiation Oncology Researcher"
      ],
      industries: [
        "Cancer Hospitals",
        "Radiation Oncology Centers", 
        "Medical Physics Departments",
        "Research Institutions",
        "Government Health Services",
        "Private Cancer Centers",
        "Medical Equipment Companies",
        "Teaching Hospitals",
        "Pharmaceutical Companies",
        "Healthcare Consulting"
      ],
      averageSalary: "₹4-9 LPA (varies by experience and specialization)",
      topRecruiters: [
        "Tata Memorial Hospital",
        "AIIMS",
        "Apollo Cancer Centers",
        "Fortis Cancer Institute", 
        "Max Cancer Care",
        "HCG Cancer Centers",
        "Manipal Comprehensive Cancer Care",
        "Varian Medical Systems",
        "Elekta",
        "Government Cancer Institutes"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Human Physiology", 
          "Radiation Physics",
          "Mathematics",
          "Basic Oncology",
          "Medical Terminology",
          "Communication Skills",
          "Computer Applications"
        ]
      },
      {
        year: 2, 
        subjects: [
          "Radiation Biology",
          "Pathology",
          "Clinical Oncology",
          "Radiation Protection",
          "Treatment Planning",
          "Radiation Therapy Equipment",
          "Patient Care",
          "Quality Assurance"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Radiotherapy",
          "Stereotactic Radiotherapy", 
          "Brachytherapy",
          "Pediatric Radiotherapy",
          "Palliative Care",
          "Clinical Training",
          "Research Methodology",
          "Professional Ethics"
        ]
      },
      {
        year: 4,
        subjects: [
          "Advanced Treatment Planning",
          "IMRT and VMAT",
          "Image-Guided Radiotherapy",
          "Quality Management",
          "Internship",
          "Project Work",
          "Continuing Education",
          "Career Development"
        ]
      }
    ],
    admissionProcess: [
      "Apply to medical colleges offering B.Sc Radiotherapy",
      "Merit-based selection or entrance examination",
      "Medical and psychological fitness assessment",
      "Document verification",
      "Interview (may be required)",
      "Fee payment and admission",
      "Radiation safety orientation",
      "Begin academic and clinical training"
    ],
    topColleges: [
      {
        name: "Tata Memorial Centre",
        location: "Mumbai, Maharashtra",
        fees: "₹1.5 LPA",
        ranking: "Premier Cancer Institute"
      },
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹10,000 PA",
        ranking: "Top Government Institute"
      },
      {
        name: "Christian Medical College", 
        location: "Vellore, Tamil Nadu",
        fees: "₹2 LPA",
        ranking: "Leading Private College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka", 
        fees: "₹4 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Regional Cancer Centre",
        location: "Thiruvananthapuram, Kerala",
        fees: "₹50,000 PA",
        ranking: "Government Cancer Center"
      }
    ],
    skills: [
      "Radiation Therapy Techniques",
      "Treatment Planning",
      "Equipment Operation", 
      "Patient Positioning",
      "Radiation Safety",
      "Quality Control",
      "Compassionate Care",
      "Attention to Detail",
      "Technical Problem Solving",
      "Team Collaboration", 
      "Stress Management",
      "Continuous Learning"
    ],
    futureScope: "Radiotherapy technology is advancing rapidly with developments in stereotactic radiosurgery, proton therapy, and AI-assisted treatment planning. Future opportunities include specialization in advanced techniques like adaptive radiotherapy, MR-guided radiotherapy, and particle therapy. The integration of artificial intelligence and machine learning in treatment planning and quality assurance is creating new career paths in medical physics and radiation oncology technology.",
    faqs: [
      {
        question: "Is there radiation exposure risk in this profession?",
        answer: "Radiation therapists follow strict safety protocols and use protective equipment. With proper training and safety measures, radiation exposure is kept well below safety limits and is continuously monitored."
      },
      {
        question: "What are the emotional challenges of working with cancer patients?",
        answer: "Working with cancer patients can be emotionally demanding. Programs include training in patient psychology, counseling skills, and stress management to help professionals provide compassionate care while maintaining emotional well-being."
      },
      {
        question: "Can I specialize in specific types of radiation therapy?",
        answer: "Yes, you can specialize in areas like stereotactic radiosurgery, brachytherapy, pediatric radiotherapy, or advanced techniques like proton therapy through additional training and certification."
      },
      {
        question: "What is the job outlook for radiation therapists?",
        answer: "The job outlook is excellent due to increasing cancer incidence and advancing treatment technologies. There's high demand for skilled radiation therapists in both urban and developing healthcare markets."
      },
      {
        question: "Are there research opportunities in radiotherapy?",
        answer: "Yes, research opportunities exist in treatment technique development, radiation biology, treatment planning optimization, and clinical trials for new radiotherapy approaches."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {radiotherapyData.title} ({radiotherapyData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={radiotherapyData} />
      </div>
    </div>
  );
};

export default RadiotherapyTechnologyCoursePage;